import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import { AsmAuthStorageService, CsAgentAuthService, TokenTarget } from '@spartacus/asm/root'
import {
  AuthActions,
  AuthService,
  OAuthLibWrapperService,
  OCC_CART_ID_CURRENT,
  OCC_USER_ID_ANONYMOUS,
  OCC_USER_ID_CURRENT,
  WindowRef
} from '@spartacus/core'
import { UserProfileService } from '@spartacus/user/profile/core'
import { CookieService } from 'ngx-cookie-service'
import { Observable } from 'rxjs'
import { map, take } from 'rxjs/operators'
import { EbAuthStatePersistenceService } from '../../auth/services/eb-auth-state-persistence.service'
import { EbUserIdService } from '../../auth/services/eb-user-id.service'
import { EbMultiCartService } from '../../cart/services/eb-multi-cart.service'
import { EbUserAccountService } from '../../user/services/eb-user-account.service'
import { EbAsmAdapter } from '../adapters/eb-asm.adapter'

/**
 * Auth service for CS agent. Useful to login/logout agent, start emulation
 * or get information about the status of emulation.
 */
@Injectable({
  providedIn: 'root'
})
export class EbCsagentAuthService extends CsAgentAuthService {
  constructor(
    protected authService: AuthService,
    protected authStorageService: AsmAuthStorageService,
    protected userIdService: EbUserIdService,
    protected oAuthLibWrapperService: OAuthLibWrapperService,
    protected store: Store,
    protected userService: UserProfileService,
    protected asmAdapter: EbAsmAdapter,
    protected userAccountService: EbUserAccountService,
    protected cookieService: CookieService,
    protected authStatePersistenceService: EbAuthStatePersistenceService,
    protected winRef: WindowRef,
    protected multiCartService: EbMultiCartService
  ) {
    super(
      authService,
      authStorageService,
      userIdService,
      oAuthLibWrapperService,
      store,
      userAccountService
    )
  }

  public startCustomerEmulationSession(customerId: string): void {
    this.authStorageService.clearEmulatedUserToken()
    this.store.dispatch(new AuthActions.Logout())
    this.asmAdapter
      .loadAsmClientSession(customerId)
      .pipe(
        take(1),
        map(() => {
          this.store.dispatch(new AuthActions.Login())
          this.userIdService.setUserEmulatedState(true)
          this.userIdService.setUserId(OCC_USER_ID_CURRENT)
          this.multiCartService.loadCart({
            cartId: OCC_CART_ID_CURRENT,
            userId: OCC_USER_ID_CURRENT,
            extraData: {
              active: true
            }
          })
        })
      )
      .subscribe()
  }

  public restoreCustomerEmulationSession() {
    if (this.winRef.localStorage.getItem('emulatedUserToken')) {
      this.authStorageService.setEmulatedUserToken({
        access_token: this.winRef.localStorage.getItem('emulatedUserToken'),
        access_token_stored_at: new Date().toDateString()
      })
    }
    this.store.dispatch(new AuthActions.Login())
    this.userIdService.setUserEmulatedState(true)
  }

  async authorizeCustomerSupportAgent(userId: string, password: string): Promise<void> {
    this.setAsmAgent(userId).subscribe(() => {
      if (this.accessToken) {
        this.authStorageService.setEmulatedUserToken({
          access_token: this.accessToken,
          access_token_stored_at: new Date().toDateString()
        })
        this.persistEmulatedUserTokenInStorage()
        this.store.dispatch(new AuthActions.Login())
        this.userIdService.setUserEmulatedState(true)
      } else {
        // When we can't get the customerId just end all current sessions
        this.userIdService.setUserId(OCC_USER_ID_ANONYMOUS)
        this.authStorageService.clearEmulatedUserToken()
      }
      this.authStorageService.switchTokenTargetToCSAgent()
      this.oAuthLibWrapperService.authorizeWithPasswordFlow(userId, password)
    })
  }

  logoutCustomerSupportAgent(): Promise<void> {
    const emulatedToken = this.authStorageService.getEmulatedUserToken()
    if (emulatedToken) {
      this.logoutAsmAgent(true).subscribe(() => {
        this.userIdService.setUserEmulatedState(false)
        this.authStorageService.setTokenTarget(TokenTarget.User)
        this.cookieService.set('temp_customer_emulation_token', emulatedToken.access_token, {
          secure: true,
          expires: 0.001,
          path: '/',
          sameSite: 'None'
        })
        this.authStorageService.clearEmulatedUserToken()
        this.winRef.localStorage.removeItem('emulatedUserToken')
      })
    } else if (!emulatedToken) {
      this.logoutAsmAgent().subscribe(() => {
        this.userIdService.setUserEmulatedState(false)
        this.authStorageService.setTokenTarget(TokenTarget.User)
        this.authStatePersistenceService.internalLogout(true, '/')
        this.userAccountService.setEbActiveUserData(undefined)
        this.store.dispatch(new AuthActions.Logout())
      })
    }
    return new Promise(() => {})
  }

  setAsmAgent(agentId: string): Observable<any> {
    return this.asmAdapter.setAgentSession(agentId).pipe(take(1))
  }

  logoutAsmAgent(returningToCustomerSession = false): Observable<any> {
    return this.asmAdapter.logoutAsmAgent(returningToCustomerSession).pipe(take(1))
  }

  logoutAsmCustomer(): Observable<any> {
    return this.asmAdapter.logoutAsmCustomer()
  }

  private persistEmulatedUserTokenInStorage() {
    this.winRef.localStorage.setItem('emulatedUserToken', this.accessToken)
  }

  get accessToken() {
    return (
      this.cookieService.get('access_token') ||
      this.cookieService.get('temp_customer_emulation_token')
    )
  }
}
